import { baseApi } from "../../redux/baseAPI";

export const providerNoteApi = baseApi.injectEndpoints({
  tagTypes: [
    "ProviderNote",
    "UserPermissions",
    "SelectedNoteSettings",
    "SelectedNoteUsersPreferences",
    "PracticeConfig",
    "ProviderNotePreview",
    "GetSignedEncounter",
    "PastSignedEncounters",
    "PastSignedEncountersNotesSections",
    "ProviderNoteAPMedications",
    "ProviderNoteAPLabs",
    "ProviderNoteAPRadiology",
    "ProviderNoteAPReferrals",
    "ProviderNoteAPProcedure",
    "ProviderNoteAPEducation",
    "ProviderNoteIcdRelatedAssessments",
    "ProviderNoteSectionRelatedOldData",
    "ProviderNoteDocumate",
    "ProviderNoteDocumatePreview",
    "ProviderNoteSectionsFullList",
    "QualityMeasureQuestions",
    "GetSavedEnumCodes"
  ],
  endpoints: (builder) => ({
    getProviderNote: builder.query({
      query: (noteId) => {
        return {
          url: `/notes/get_note.json`,
          method: "GET",
          params: { id: noteId, include_thread: "1", e10x: "1" },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNote"],
    }),
    getUserPermissions: builder.query({
      query: () => {
        return {
          url: `/acl/get_user_permissions.json`,
          method: "GET",
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["UserPermissions"],
    }),
    getSelectedNoteSettings: builder.query({
      query: (payload) => {
        if (payload?.pid) {
          return {
            url: `/encounters/settings.json`,
            method: "GET",
            params: {
              cases: "all",
              pid: payload.pid,
              encounter: payload.encounter,
            },
          };
        }
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["SelectedNoteSettings"],
    }),
    getUsersPreferences: builder.query({
      query: () => {
        return {
          url: `/users/preferences.json`,
          method: "GET",
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["SelectedNoteUsersPreferences"],
    }),
    setUsersPreferences: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/users/set_preferences.json`,
        method: "POST",
        data: body,
      }),
      transformResponse: (res, meta, arg) => res.response,
      invalidatesTags: ["SelectedNoteUsersPreferences"],
    }),
    getPracticeConfig: builder.query({
      query: () => {
        return {
          url: `/practices/get_config.json`,
          method: "GET",
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["PracticeConfig"],
    }),
    getProviderNoteAPMedications: builder.query({
      query: () => {
        return {
          url: `/notes/get_favorite_components.json`,
          method: "GET",
          params: { components: "medication" },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNoteAPMedications"],
    }),
    getProviderNoteAPLabs: builder.query({
      query: () => {
        return {
          url: `/notes/get_favorite_components.json`,
          method: "GET",
          params: { components: "lab" },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNoteAPLabs"],
    }),
    getProviderNoteAPRadiology: builder.query({
      query: () => {
        return {
          url: `/notes/get_favorite_components.json`,
          method: "GET",
          params: { components: "radiology" },
        };
      },
      transformResponse: (res, meta, arg) => res?.response?.data,
      providesTags: ["ProviderNoteAPRadiology"],
    }),
    getProviderNoteAPReferrals: builder.query({
      query: () => {
        return {
          url: `/notes/get_favorite_components.json`,
          method: "GET",
          params: { components: "referral" },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNoteAPReferrals"],
    }),
    getProviderNoteAPProcedure: builder.query({
      query: () => {
        return {
          url: `/notes/get_favorite_components.json`,
          method: "GET",
          params: { components: "procedure" },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNoteAPProcedure"],
    }),
    getProviderNoteAPEducation: builder.query({
      query: (pid) => {
        return {
          url: `/intervention/recomended_education.json`,
          method: "GET",
          params: { pid: pid, components: "education" },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNoteAPEducation"],
    }),
    getProviderNotePreview: builder.query({
      query: (encounterId) => {
        return {
          url: `/notes/preview_section.json`,
          method: "GET",
          params: { encounter: encounterId },
        };
      },
      transformResponse: (res, meta, arg) => res.response,
      providesTags: ["ProviderNotePreview"],
    }),
    getSignedEncounterPreview: builder.query({
      query: (encounterId) => {
        return {
          url: `/notes/preview_section.json`,
          method: "GET",
          params: {
            encounter: encounterId === "object"
            ? encounterId?.encounter
            : encounterId,
            force_encounter:
              typeof encounterId === "object"
                ? encounterId?.encounter
                : encounterId,
          },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["GetSignedEncounter"],
    }),
    getPastSignedEncounterPreview: builder.query({
      query: (payload) => {
        return {
          url: `/encounters/index.json`,
          method: "GET",
          params: {
            ...payload,
          },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["PastSignedEncounters"],
    }),
    getNotesSections: builder.query({
      query: (payload) => {
        return {
          url: `/provider_notes/get_sections.json`,
          method: "GET",
          params: {
            ...payload,
          },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["PastSignedEncountersNotesSections"],
    }),
    getProviderNoteIcdRelatedAssessments: builder.query({
      query: (body) => {
        return {
          url: `/notes/get_prev_asmts_by_icd.json`,
          method: "GET",
          params: { encounter: body.encounter, icd10_code: body.icd10_code },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNoteIcdRelatedAssessments"],
    }),
    getNoteSectionRelatedOldData: builder.query({
      query: (body) => {
        return {
          url: `/notes/get_past_sections.json`,
          method: "GET",
          params: {
            encounter: body.encounter,
            pid: body.pid,
            section: body.section,
          },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ProviderNoteSectionRelatedOldData"],
    }),
    saveSectionText: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/notes/save_text.json`,
        method: "POST",
        data: checkSaveTextData(body),
        params: { pid: body.pid },
      }),
      transformResponse: (res, meta, arg) => res.response,
    }),
    saveClinicalData: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/provider_notes/update_clinical_data.json`,
        method: "POST",
        data: body,
        params: { pid: body.pid, skip_auto_accept: body.skip_auto_accept ? body.skip_auto_accept : 0  },
      }),
      transformResponse: (res, meta, arg) => res.response,
    }),
    updateAPClinicalData: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/notes/update_ap_problem.json`,
        method: "POST",
        data: body,
        params: { pid: body.pid, skip_auto_accept: body.skip_auto_accept ? body.skip_auto_accept : 0  },
      }),
      transformResponse: (res, meta, arg) => res.response,
    }),
    linkComponentDataWithNote: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body, isBuild = false) => {
        return {
          url: `/notes/accept.json`,
          method: "POST",
          data: body?.isBuild ? body : buildAcceptJsonData(body),
          params: { skip_auto_accept: body.skip_auto_accept ? 1 : 0 },
        };
      },
    }),

    searchEducation: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => {
        return {
          url: `/intervention/search_education.json`,
          method: "GET",
          params: { ...body },
        };
      },
      // transformResponse: (res, meta, arg) => res.response.data,
      transformResponse: (res, meta, arg) => res,
      //invalidatesTags: ["ProviderNote"],
    }),
    signNote: builder.mutation({
      query: (body) => {
        return {
          url: `/notes/sign_note.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    saveHippaReferralCode: builder.mutation({
      query: (body) => {
        return {
          url: `/encounters/save_hipaa_referral_code.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    getNotePreview: builder.mutation({
      query: (body) => {
        return {
          url: `/notes/get_preview_sections.json?audit_encounter_id=${body.audit_encounter_id}`,
          method: "POST",
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    printNote: builder.mutation({
      query: (body) => {
        return {
          url: `/notes/selected_print.json?encounter_id=${body?.encounter_id}`,
          method: "POST",
          data: { notes: body?.notes },
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    prepareData: builder.mutation({
      query: (body) => {
        console.log("encounter_id_", body);
        return {
          url: `/share_data/prepare_data.json?audit_encounter_id=${body?.encounter_id}`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),

    printSummary: builder.mutation({
      query: (body) => {
        return {
          url: `/notes/print_summary.json?encounter_id=${body?.encounter_id}`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    getEncounterTemplate: builder.query({
      query: (encounterId) => {
        return {
          url: `/encounters/encounter_template_settings.json`,
          method: "GET",
          params: "",
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      //providesTags: ["ProviderNotePreview"],
    }),
    getEncounterType: builder.query({
      query: (encounterId) => {
        return {
          url: `/encounters/get_templates_by_user.json`,
          method: "GET",
          params: "",
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      //providesTags: ["ProviderNotePreview"],
    }),
    getEncounterSetting: builder.query({
      query: (encounterId) => {
        return {
          url: `/encounters/global_search_settings.json`,
          method: "GET",
          params: "",
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      //providesTags: ["ProviderNotePreview"],
    }),
    getEncountersList: builder.query({
      query: (payload) => {
        return {
          url: `/encounters.json`,
          method: "GET",
          params: { ...payload },
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      //providesTags: ["ProviderNotePreview"],
    }),
    getEncounterRoles: builder.query({
      query: (encounterId) => {
        return {
          url: `/utilities/listing/careteam_role.json`,
          method: "GET",
          params: "",
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      //providesTags: ["ProviderNotePreview"],
    }),
    saveEncounter: builder.mutation({
      query: (body) => {
        return {
          url: `/encounters/create.json`,
          method: "POST",
          data: body,
        };
      },
      //providesTags: ["ProviderNotePreview"],
      transformResponse: (response, meta, arg) => response.response,
    }),

    addAttachments: builder.mutation({
      query: ({ body, pid }) => {
        return {
          url: `/utilities/multiple_attachments.json`,
          method: "POST",
          headers: {
            "content-type": "multipart/form-data",
          },
          data: body,
          params: { pid: pid },
        };
      },
      // invalidatesTags: ['Result'],
      // invalidatesTags: ['ProviderNoteAPLabs'],
      transformResponse: (response, meta, arg) => response.response,
    }),
    addReason: builder.mutation({
      query: (body) => {
        return {
          url: `/encounters/add_reason.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    changeTemplate: builder.mutation({
      query: (body) => {
        return {
          url: `/encounters/change_template.json`,
          method: "POST",
          params: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    supperBillPreview: builder.mutation({
      query: (body) => {
        return {
          url: `/billing/superbills_preview.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    shareESupperBill: builder.mutation({
      query: (body) => {
        return {
          url: `/billing/share_superbills.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    deleteEncounter: builder.mutation({
      query: (body) => {
        return {
          url: `/encounters/update_status.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    addAddendum: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/notes/add_addendum.json`,
        method: "POST",
        data: body,
        params: { pid: body.pid },
      }),
      transformResponse: (res, meta, arg) => res.response,
    }),
    unSignNote: builder.mutation({
      query: () => {
        return {
          url: `/notes/note_unsign_for_addendum.json`,
          method: "POST",
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    getDocumate: builder.query({
      query: (noteId) => {
        return {
          url: `/notes/documate.json`,
          method: "GET",
        };
      },
      transformResponse: (res, meta, arg) => res.response,
      providesTags: ["ProviderNoteDocumate"],
    }),

    autoGenerateEsuperBill: builder.query({
      query: (noteId) => {
        return {
          url: `/notes/auto_generate_esuperbill.json`,
          method: "GET",
        };
      },
      transformResponse: (res, meta, arg) => res.response,
    }),

    getDocumatePreview: builder.query({
      query: (noteId) => {
        return {
          url: `/notes/preview_for_documate.json`,
          method: "GET",
          params: { force_encounter: noteId },
        };
      },
      transformResponse: (res, meta, arg) => res.response,
      providesTags: ["ProviderNoteDocumatePreview"],
    }),
    getNoteSectionsFullList: builder.query({
      query: (noteId) => {
        return {
          url: `/notes/get_note_sections_full_list.json`,
          method: "GET",
          params: { force_encounter: noteId },
        };
      },
      transformResponse: (res, meta, arg) => res.response,
      providesTags: ["ProviderNoteSectionsFullList"],
    }),
    enableDisableSections: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/notes/enable_disable_sections.json`,
        method: "POST",
        data: body,
        params: { force_encounter: body.encounter },
      }),
      transformResponse: (res, meta, arg) => res.response,
      invalidatesTags: ["ProviderNote", "ProviderNoteSectionsFullList"],
    }),
    getQualityMeasureQuestions: builder.query({
      query: (body) => {
        return {
          url: `/mips_settings/qualified_measure_questions.json`,
          method: "GET",
          params: body,
        };
      },
      // transformResponse: (res, meta, arg) => res.response.data,
      transformResponse: (response, meta, arg) => {
        if (response?.response?.data?.data && Array.isArray(response?.response?.data?.data)) {
          let measures = response?.response?.data?.data?.map((measure) => {
            let questions = [];
            // find parent questions
            measure.questions.forEach((ques) => {
              if (ques.parent_id == "0") {
                ques.child_questions = [];
                questions.push(ques);
              }
            });

            //attach child questions with parent
            measure.questions.forEach((ques) => {
              if (ques.parent_id != "0") {
                var foundIndex = questions.findIndex(function (question) {
                  return question.question_id === ques.parent_id;
                });
                if (foundIndex > -1) {
                  questions[foundIndex].child_questions.push(ques);
                }
              }
            });

            measure.questions = questions;
            return measure;
          });

          return {
            measures,
            total: measures.length > 0 ? measures.length : 0,
            redCount:
              response?.response?.data?.reds > 0
                ? response?.response?.data?.reds
                : 0,
            greenCount:
              response?.response?.data?.greens > 0
                ? response?.response?.data?.greens
                : 0,
          };
        } else {
          return response?.response?.data;
        }
      },
      providesTags: ["QualityMeasureQuestions"],
    }),
    saveMeasureQuestion: builder.mutation({
      query: (body) => {
        return {
          url: `/procedures/save_procedure.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
      invalidatesTags: ["QualityMeasureQuestions"],
    }),
    savMeasureQuestionsForPreview: builder.mutation({
      query: (body) => {
        return {
          url: `/mips_settings/save_mips_answer_for_preview.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
      // invalidatesTags: ["QualityMeasureQuestions"],
    }),
    editAssessmentAndPlanActions: builder.query({
      query: (body) => {
        return {
          url: `/notes/del_fav_comp_items.json`,
          method: "GET",
          params: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
      invalidatesTags: ["QualityMeasureQuestions"],
    }),
    updateForcedSign: builder.mutation({
      query: (body) => {
        return {
          url: `/notes/update_forced_sign.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    assignNote: builder.mutation({
      query: (body) => {
        return {
          url: `/encounters/add_encounter_history_log.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    addCosignReview: builder.mutation({
      query: (body) => {
        return {
          url: `/encounters/add_cosign_review.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    changeScribeNote: builder.mutation({
      query: (body) => {
        return {
          url: `/notes/update_scribe.json`,
          method: "POST",
          params: { id: body?.encounter },
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
    }),
    saveEncounterDuration: builder.mutation({
      query: (body) => {
        return {
          url: `/encounter_enm_codes/save_duration.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
      invalidatesTags: ["GetSavedEnumCodes"],
    }),
    saveEnumCodes: builder.mutation({
      query: (body) => {
        return {
          url: `/encounter_enm_codes/save_enm_codes.json`,
          method: "POST",
          data: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
      invalidatesTags: ["GetSavedEnumCodes"]
    }),
    getSavedEnumCodes: builder.query({
      query: (body) => {
        return {
          url: `/encounter_enm_codes/index.json`,
          method: "GET",
          params: body,
        };
      },
      transformResponse: (response, meta, arg) => response.response,
      providesTags: ["GetSavedEnumCodes"],
    }),
     assignProviderToEncounter: builder.mutation({
      query: (body) => ({
        url: `/encounters/assign_rendering_provider.json`,
        method: "POST",
        data: body,
        params: { pid: body.pid },
      }),
      transformResponse: (response, meta, arg) => response.response,
      invalidatesTags: ["ProviderNote"],

    }),
    shareNoteOnPatientPortal: builder.mutation({
      // note: an optional `queryFn` may be used in place of `query`
      query: (body) => ({
        url: `/encounters/push_on_patient_portal.json`,
        method: "POST",
        data: body,
      }),
      transformResponse: (res, meta, arg) => res.response,
    }),
    getReviewList: builder.query({
      query: (params) => {
        return {
          url: `/charge_reviews/search_list.json`,
          method: "GET",
          params: params
        };
      },
      transformResponse: (res, meta, arg) => res.response.data,
      providesTags: ["ReviewList"] 
    }),
    addReview: builder.mutation({
      query: (body) => ({
        url: `/charge_reviews/save.json`,
        method: "POST",
        data: body
      }),
      transformResponse: (response, meta, arg) => response.response,
      invalidatesTags: ["ReviewList"] 
    }),
    getNoteChecks: builder.query({
      query: (body) => {
        return {
          url: `/notes/note_checks.json`,
          method: "GET",
          params: body,
        };
      },
      providesTags: ["NoteChecks"],
      transformResponse: (response, meta, arg) => response.response,
    }),
  }),
});

export const {
  useGetProviderNoteQuery,
  useGetUserPermissionsQuery,
  useGetSelectedNoteSettingsQuery,
  useGetUsersPreferencesQuery,
  useGetPracticeConfigQuery,
  useSetUsersPreferencesMutation,
  useGetProviderNoteAPMedicationsQuery,
  useGetProviderNoteAPLabsQuery,
  useGetProviderNoteAPRadiologyQuery,
  useGetProviderNoteAPReferralsQuery,
  useGetProviderNoteAPProcedureQuery,
  useGetProviderNoteAPEducationQuery,
  useGetProviderNotePreviewQuery,
  useGetSignedEncounterPreviewQuery,
  useGetPastSignedEncounterPreviewQuery,
  useGetNotesSectionsQuery,
  useGetProviderNoteIcdRelatedAssessmentsQuery,
  useGetNoteSectionRelatedOldDataQuery,
  useSaveSectionTextMutation,
  useSaveClinicalDataMutation,
  useUpdateAPClinicalDataMutation,
  useLinkComponentDataWithNoteMutation,
  useSearchEducationMutation,
  useSignNoteMutation,
  useSaveHippaReferralCodeMutation,
  useGetNotePreviewMutation,
  usePrintNoteMutation,
  usePrepareDataMutation,
  usePrintSummaryMutation,
  useGetEncounterTemplateQuery,
  useGetEncounterTypeQuery,
  useGetEncounterSettingQuery,
  useSaveEncounterMutation,
  useAddAttachmentsMutation,
  useGetEncounterRolesQuery,
  useAddReasonMutation,
  useChangeTemplateMutation,
  useSupperBillPreviewMutation,
  useShareESupperBillMutation,
  useGetEncountersListQuery,
  useDeleteEncounterMutation,
  useEditAssessmentAndPlanActions,

  useAddAddendumMutation,
  useUnSignNoteMutation,

  useGetDocumateQuery,
  useGetDocumatePreviewQuery,
  useGetNoteSectionsFullListQuery,
  useEnableDisableSectionsMutation,
  useGetQualityMeasureQuestionsQuery,
  useSaveMeasureQuestionMutation,
  useSavMeasureQuestionsForPreviewMutation,
  useUpdateForcedSignMutation,
  useAssignNoteMutation,
  useAddCosignReviewMutation,
  useChangeScribeNoteMutation,
  useSaveEncounterDurationMutation,
  useSaveEnumCodesMutation,
  useGetSavedEnumCodesQuery,
  useAssignProviderToEncounterMutation,
  useShareNoteOnPatientPortalMutation,
  useGetReviewListQuery,
  useAddReviewMutation,
  useGetNoteChecksQuery,
} = providerNoteApi;

export const checkSaveTextData = (body) => {
  if (body?.notes && body?.notes?.length) {
    body.notes = body?.notes?.map((note) => {
        if (!body?.skip_xlu && note?.skip_note_text) {
          delete note.skip_note_text;
        }
        if (note?.socket_update) {
          delete note.socket_update;
        }
        if (note?.update) {
          delete note.update;
        }
        return note;
    });
    return body;
  } else {
    return body;
  }
}
export const buildAcceptJsonData = (body) => {
  let postData = {
    encounter_id: body.note.encounter,
    component: body.slug,
    ids: "",
    edata: [],
    extra_data: [],
    e10x_favorite: body?.e10x_favorite ? true : false,
  };
  // if(body.extracted){
  //   body.extracted.map((v)=>(
  //     postData.order_type = v.order_type
  //   ))
  // }
  if (postData.component == "health_conditions") {
    if (typeof body.extracted !== "undefined" && body.extracted) {
      // CLU Extracted data at root
      postData.edata = body.extracted;
      postData.edata.forEach((prb, prbInd) => {
        if (prb?.code) {
          if (prb?.id) {
            postData.edata[prbInd].id = prb?.id;
          }
          postData.edata[prbInd].title = prb?.short_desc;
          postData.edata[prbInd].is_active = true;
          postData.edata[prbInd].is_chronic = true;
          postData.edata[prbInd].is_deleted = false;
          postData.edata[prbInd].selected = true;
          postData.edata[prbInd].matched = true;
        } else {
          postData.edata[prbInd].is_deleted = true;
          postData.edata[prbInd].is_active = false;
          postData.edata[prbInd].code = "";
        }
      });
    }
    if (typeof body.active !== "undefined" && body.active.length) {
      // Module active data in clinical_data
      postData.ids =
        body.active
          .filter((prob) => {
            return prob.id;
          })
          .map((prob) => {
            return prob.id;
          }) + "";
    }
    let ImportAssessmentPlan =
      typeof body?.note?.clinical_data?.import_assessment_plan
        ?.import_assessment_plan !== "undefined"
        ? body?.clinical_data?.import_assessment_plan?.import_assessment_plan
        : body?.note?.import_assessment_problems
        ? true
        : false;
    postData.extra_data.push({
      component: "import_assessment_plan",
      ids: "",
      edata: {
        import_assessment_plan: {
          import_assessment_plan: ImportAssessmentPlan ? true : false,
        },
      },
    });
  } else if (postData.component == "histories") {
    if (typeof body.active !== "undefined" && body.active.length) {
      // Module active data in clinical_data
      postData.ids = body.active.toString();
    }
    postData.extra_data.push({
      component: "dynamic_history",
      ids: (body?.dynamicHistoryIDs) ? body?.dynamicHistoryIDs.toString() : "",
      edata: {},
    });
  } else {
    if (typeof body.active !== "undefined" && body.active.length) {
      // Module active data in clinical_data
      postData.ids = body.active.toString();
    }
    if (typeof body.extracted !== "undefined") {
      // Extracted data at root
      postData.edata = body.extracted;
    }
  }
  return postData;
};
